import { apiService } from './api';
import { logger } from '../utils/logger';


export interface BookImportError {
  row: number;
  field: string;
  error: string;
  value: string;
}

export interface BookImportSuccess {
  row: number;
  title: string;
  author: string;
  bookId: string;
}

export interface BookImportResult {
  success: boolean;
  totalRows: number;
  successfulImports: number;
  failedImports: number;
  errors: BookImportError[];
  successes: BookImportSuccess[];
  message: string;
}

export const bookImportService = {
  /**
   * Import books from Excel/CSV file
   */
  async importBooks(file: File): Promise<BookImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    logger.log('📤 Sending FormData to API...', file.name);

    // Use fetch directly for FormData to avoid JSON.stringify issues
    // Use relative path to go through Vite proxy to postgres-api-server
    // Get auth token from localStorage
    const authToken = localStorage.getItem('authToken');

    const headers: HeadersInit = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Add user ID from sessionStorage if available
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          headers['x-user-id'] = user.id;
        }
      }
    } catch (_) {
      // ignore
    }

    const schoolCode = localStorage.getItem('schoolCode');
    if (schoolCode) {
      headers['x-school-code'] = schoolCode;
    }

    const response = await fetch(`/api/books/import`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include', // Include cookies for httpOnly token storage
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    logger.log('📥 API response received:', result);
    // Ensure success field is present for frontend logic
    if (result.success === undefined) {
      result.success = true;
    }
    return result;
  },

  /**
   * Download Excel template for book import
   */
  async downloadTemplate(): Promise<void> {
    // Use fetch directly to get Excel .xlsx file
    // Use relative path to go through Vite proxy to postgres-api-server
    // Get user ID from sessionStorage for authentication (same as userImportService)
    const userData = sessionStorage.getItem('user');
    const userId = userData ? JSON.parse(userData).id : null;

    // Also get auth token as fallback
    const authToken = localStorage.getItem('authToken');

    // Debug: Log to console and also show alert if userId is missing
    if (!userId && !authToken) {
      const errorMsg = 'Không tìm thấy thông tin đăng nhập. Vui lòng đăng nhập lại.';
      logger.error('❌', errorMsg);
      alert(errorMsg);
      throw new Error(errorMsg);
    }

    logger.log('📥 Download template - userId:', userId, 'authToken:', authToken ? 'exists' : 'missing');

    const headers: HeadersInit = {};

    // Priority 1: Use x-user-id header (backend prefers this)
    if (userId) {
      headers['x-user-id'] = String(userId);
    }

    // Priority 2: Use Authorization header as fallback
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const schoolCode = localStorage.getItem('schoolCode');
    if (schoolCode) {
      headers['x-school-code'] = schoolCode;
    }

    logger.log('📥 Download template - headers:', {
      'x-user-id': headers['x-user-id'],
      'x-school-code': headers['x-school-code'],
      'Authorization': headers['Authorization'] ? 'Bearer ***' : undefined
    });

    const response = await fetch(`/api/books/template`, {
      method: 'GET',
      headers,
      credentials: 'include', // Include cookies for httpOnly token storage
    });

    logger.log('📥 Download template - response status:', response.status, response.statusText);

    // If 401, try to get error message from response
    if (response.status === 401) {
      let errorMessage = 'Không có quyền truy cập. Vui lòng đăng nhập lại.';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If response is not JSON, use default message
      }
      logger.error('❌ 401 Unauthorized:', errorMessage);
      throw new Error(errorMessage);
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    // Get Excel file as blob
    const blob = await response.blob();
    logger.log('📄 Excel file received:', blob.size, 'bytes'); // Debug log

    // Create blob link to download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'book-import-template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
